import React, { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import { setUser } from '../lib/authSlice';
import {
  startImpersonation, createImpersonationLink, isImpersonating, impersonationLabel,
} from '../lib/impersonation';

/**
 * Split button: "Impersonate" switches this session in place; the caret offers
 * one-time links instead.
 *
 * The two modes answer different needs. Switching is fastest when you just need
 * to BE them for a minute. A link opened in a private window lets you be
 * yourself and them at the same time — two windows, side by side — which is
 * what you want when comparing what they see against what you see.
 *
 * Renders nothing for accounts that can't be impersonated (admins) or while a
 * session is already live, so there's never a question of who you currently
 * are.
 */
export default function ImpersonateButton({ user, className = '' }) {
  const [busy, setBusy] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (!user || user.role === 'admin') return null;
  if (isImpersonating()) return null;

  const label = impersonationLabel(user);
  // The admin app has no shell for consumer accounts — only the PWA can host
  // that session, so the admin-side options are hidden rather than offered and
  // then refused by the server.
  const canUseAdminSurface = user.role === 'industry';

  const switchInPlace = async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Act as ${label}?\n\nYou'll see the site exactly as they do, and anything you change is saved to their real account. Every change is recorded in the listing's change history with your name on it.\n\nThe session ends in an hour, or when you press "Switch back".`,
    );
    if (!ok) return;

    setBusy(true);
    try {
      const target = await startImpersonation(user.id);
      dispatch(setUser(target));
      navigate('/industry/');
    } catch (e) {
      toast.error(e.message || 'Could not start the session.');
      setBusy(false);
    }
  };

  const copyLink = async (surface) => {
    setBusy(true);
    try {
      const result = await createImpersonationLink(user.id, surface);
      await navigator.clipboard.writeText(result.url);
      toast.success(
        `${surface === 'client' ? 'App' : 'Admin'} link copied — paste it into a private window. It works once, within ${result.linkTtlMinutes} minutes.`,
        { duration: 6000 },
      );
    } catch (e) {
      // A clipboard write can fail on an insecure origin; the link is already
      // minted at that point, so show it rather than losing it silently.
      const message = e && e.message ? e.message : 'Could not create the link.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const itemClass = (active) => `block w-full px-4 py-2 text-left text-sm ${active ? 'bg-amber-50 text-amber-900' : 'text-gray-700'}`;

  return (
    <div className={`inline-flex rounded-md shadow-sm ${className}`}>
      {canUseAdminSurface && (
        <button
          type="button"
          onClick={switchInPlace}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-l-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {busy ? 'Working…' : 'Impersonate'}
        </button>
      )}

      <Menu as="div" className="relative -ml-px block">
        <Menu.Button
          disabled={busy}
          className={`inline-flex items-center border border-amber-300 bg-amber-50 px-2 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60 ${canUseAdminSurface ? 'rounded-r-md' : 'gap-2 rounded-md px-3'}`}
        >
          {!canUseAdminSurface && <span>Impersonation link</span>}
          <span className="sr-only">Impersonation options</span>
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-20 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <p className="px-4 pb-1 pt-2 text-xs text-gray-500">
                Open a link in a private window to be yourself and {label} at the same time.
              </p>
              {canUseAdminSurface && (
                <Menu.Item>
                  {({ active }) => (
                    <button type="button" onClick={() => copyLink('admin')} className={itemClass(active)}>
                      Copy admin link
                      <span className="block text-xs text-gray-500">Their winery management view</span>
                    </button>
                  )}
                </Menu.Item>
              )}
              <Menu.Item>
                {({ active }) => (
                  <button type="button" onClick={() => copyLink('client')} className={itemClass(active)}>
                    Copy app link
                    <span className="block text-xs text-gray-500">What they see in the VinoSeeker app</span>
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
